import Analytics from '@opencare/analytics';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { UserMarketName } from 'connect/saga/handlers/user/user-market-name';
import { selectDataMatch } from 'connect/selectors';
import { ForcedExperimentVariantsStateHandler } from 'connect/state/handlers/experiments';
import { useAnalyticsService } from 'services/AnalyticsService';
import Logger from 'utils/Logger';

import { GetExperimentVariants } from '../../connect/saga/handlers/experiment/get-experiment-variants';
import { AssignedExperiment, Experiment, ExperimentName, VARIANTS } from '../constants/experiment-configs';

const analytics = new Analytics();

const findExperimentVariant = (experimentVariants: AssignedExperiment[], experimentName: ExperimentName) => {
  const matchingExperiments = experimentVariants.filter(
    (variant) => variant.name === experimentName,
  );

  if (matchingExperiments.length <= 0) {
    return null;
  }

  if (matchingExperiments.length > 1) {
    Logger.error(
      `More than one variant found for experiment ${experimentName}`,
    );
  }

  return matchingExperiments[0]!.variant.variantNumber;
};

interface UseExperimentProps {
  experiment: Experiment;
  condition?: boolean;
  skipTracking?: boolean;
}

interface ExperimentResult {
  variant: string;
  isControl: boolean;
  isVariant: boolean;
}

const createResult = ({ variantNumber }: { variantNumber: string | null; }): ExperimentResult | null => {
  if (variantNumber === null) {
    return null;
  }

  const type = variantNumber === '0' ? VARIANTS.CONTROL : VARIANTS.VARIANT;
  return {
    variant: variantNumber,
    isControl: type === VARIANTS.CONTROL,
    isVariant: type === VARIANTS.VARIANT,
  };
};

export const useExperiment = ({ experiment, condition = true, skipTracking = false }: UseExperimentProps) => {
  const dispatch = useDispatch();
  const analyticsService = useAnalyticsService();

  const userMarket = useSelector(UserMarketName.value!.selector);
  const userMarketLoading = useSelector(UserMarketName.loading!.selector);
  const match = useSelector(selectDataMatch);
  const experimentVariants = useSelector(GetExperimentVariants.value!.selector);
  const forcedExperimentVariants = useSelector(
    ForcedExperimentVariantsStateHandler.selector,
  );

  // there are some cases where userMarket can be null (e.g. user hasn't gone through funnel location selection yet, user came directly to checkout, skipped funnel - visit from a direct link)
  // match data is often populated before the saga runs, so this serves as a fallback as we will always have match data in the match object to get a market name from
  const matchMarketName = match?.location?.marketName;
  const effectiveMarket = userMarket || matchMarketName || null;

  useEffect(() => {
    if (
      !userMarket
      && !userMarketLoading
      && match?.location?.address
      && !matchMarketName
    ) {
      dispatch(UserMarketName.createActionFromAddress(match.location.address));
    }
  }, [userMarket, userMarketLoading, match, matchMarketName, dispatch]);

  const doesConditionPass = useMemo(() => condition || condition === undefined, [condition]);

  const isMarketIncluded = useMemo(
    () => experiment.markets?.includes(effectiveMarket?.toLowerCase()) ?? true,
    [experiment.markets, effectiveMarket],
  );

  const isMarketExcluded = useMemo(
    () => experiment.excludeMarkets?.includes(effectiveMarket?.toLowerCase()) ?? false,
    [experiment.excludeMarkets, effectiveMarket],
  );

  const doesMarketMatch = useMemo(
    () => isMarketIncluded && !isMarketExcluded,
    [isMarketIncluded, isMarketExcluded],
  );

  const isExperimentApplicable = useMemo(
    () => doesConditionPass && doesMarketMatch,
    [doesConditionPass, doesMarketMatch],
  );

  const variant = useMemo(() => {
    if (!isExperimentApplicable || !experimentVariants?.length) {
      return null;
    }

    const variantToUse =
      forcedExperimentVariants?.[experiment.name]
      || findExperimentVariant(experimentVariants, experiment.name);

    if (!variantToUse) {
      Logger.error(`No variant found for experiment ${experiment.name}`);
    }

    return variantToUse;
  }, [isExperimentApplicable, experimentVariants, forcedExperimentVariants, experiment.name]);

  const [result, setResult] = useState<ExperimentResult | null>(createResult({ variantNumber: variant }));

  useEffect(
    () => setResult(createResult({ variantNumber: variant })),
    [variant],
  );

  // Track in heap when the experiment variant is assigned
  useEffect(() => {
    if (!result?.variant || skipTracking) {
      return;
    }

    analytics.trackPageView({
      pageName: 'experiment-variant-assigned',
      pageType: analytics.pageTypes.EXPERIMENT_VARIANT_ASSIGNED,
      experimentName: String(experiment.name),
      experimentVariant: result.variant,
      userMarket: effectiveMarket,
      noTrackCro: true,
      ...analyticsService.analyticsData,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every time, so we don't want it in the dependency array
  }, [result?.variant, skipTracking, experiment?.name, effectiveMarket]);

  // Fall back to a control variant
  // This is so that in the event where the experiment is not applicable,
  // or no variant is found, we can still return a result
  // that will have minimal side effects
  return result || createResult({ variantNumber: '0' })!;
};
